/**
 * Frontend observability — single entrypoint.
 *
 * At app startup (e.g. taskpane.tsx), call `bootObservability()` once to
 * wire sinks. Everywhere else: `import { getLogger } from "../observability"`.
 */

import { Logger, configure, getLogger } from "./logger";
import { ConsoleSink, MemorySink, ServerSink } from "./sinks";

// Exported singleton so DebugConsole can read the ring buffer.
export const memorySink = new MemorySink(200);

let _booted = false;

export function bootObservability(): void {
  if (_booted) return;
  _booted = true;
  // ConsoleSink mirrors log records into devtools. It is a second logging
  // channel that the build's console-stripping cannot reach: the sink
  // picks console.debug/info/warn/error dynamically by record level, so
  // esbuild sees method references rather than droppable calls.
  //
  // Production therefore raises its floor to "error", matching the policy
  // in vite.config.ts — errors still surface, the debug/info/warn stream
  // does not. MemorySink keeps feeding the in-app DebugConsole and
  // ServerSink keeps shipping to /api/debug-log either way, so nothing is
  // actually lost, it just stops being printed.
  configure([
    new ConsoleSink(import.meta.env.DEV ? "debug" : "error"),
    memorySink,
    new ServerSink("/api/debug-log", "info"),
  ]);
  getLogger("frontend.boot").info("observability.ready");
}

export { getLogger, Logger, configure };
export type { LogLevel, LogRecord, LogSink } from "./logger";
